import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant, useAuthUser } from 'store/auth';
import { DefaultTable } from 'Components/common/DefaultTable';
import { Main as Container } from 'Components/Main';
import { SectionBanner, DefaultPagination } from 'Components/common';
import { tableHeader } from './table-header.js';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Controller, useForm } from 'react-hook-form';
import { Column, Row } from 'Components/Grid';
/** Services */
import { deleteUserVerifyCode, getUsersVerifyCodesPaged } from 'services/users-service';
import { Button, Card, Dialog, PageHeader, Select, TextField } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

export const UsersVerifyCodes = () => {
  const { t } = useTranslation(['user', 'common']);
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const { control, register, reset, watch } = useForm({
    defaultValues: {
      companyKey: selectedTenant.slug,
      searchContent: '',
    },
  });

  const getContentSearch = () => {
    if (!watch('searchContent')) return '';

    return watch('searchContent');
  };

  const {
    isLoading,
    error,
    data: pagedUsersVerifyCodes,
  } = useQuery(['getUsersVerifyCodes', refetch, page], async () => {
    if (selectedTenant.slug === undefined) return [];
    const search = getContentSearch();

    const result = await getUsersVerifyCodesPaged(watch('companyKey'), page, 15, search);
    if (result?.data.data?.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteUserVerifyCode(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        Dialog.alert({
          title: t('user:alert.successTitle'),
          message: t('user:verifyCodes.success.deactivated'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar desativar o código de verificação do Usuário!');
      }
    } catch (error) {
      Dialog.alert({
        title: 'Erro!',
        message: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container>
      <PageHeader
        title={t('user:verifyCodes.title')}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('user:verifyCodes.title') },
        ]}
        actions={<Button onClick={() => navigate('add')}>{t('common:buttons.addNew')}</Button>}
      />
      <SectionBanner
        title={t('user:verifyCodes.infoTitle')}
        description={t('user:verifyCodes.description')}
      />
      <Card className="mb-2 mt-3">
        <Row>
          <Column xs={12} md={3}>
            <Controller
              control={control}
              name="companyKey"
              render={({ field: { onChange, value } }) => (
                <Select
                  name="companyKey"
                  label={t('user:verifyCodes.filter.company')}
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('user:verifyCodes.filter.selectCompany') },
                    ...(user.companiesWithAccess || []).map((item) => ({ value: item.slug, label: item.name })),
                  ]}
                />
              )}
            />
          </Column>
          <Column xs={12} md={4}>
            <TextField
              {...register('searchContent')}
              label={t('user:verifyCodes.filter.search')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Column>
          <Column xs={12} md={'auto'}>
            <Button variant="secondary" onClick={() => { reset(); setRefetch(!refetch) }}>
              {t('common:buttons.clear')}
            </Button>
          </Column>
          <Column xs={12} md={'auto'}>
            <Button startIcon={<SearchSvg />} onClick={() => { setRefetch(!refetch) }}>
              {t('common:buttons.filter')}
            </Button>
          </Column>
        </Row>
      </Card>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('user:verifyCodes.error')}</div>}
      {!isLoading && !error && pagedUsersVerifyCodes.data?.length === 0 && (
        <NoData description={t('user:verifyCodes.noData')} />
      )}
      {!isLoading && !error && pagedUsersVerifyCodes.data?.length > 0 && (
        <div className="container">
          <DefaultTable
            rows={tableHeader(t)}
            cells={pagedUsersVerifyCodes.data}
            onClickDelete={onDelete}
            link="users-verify-codes"
            hideEdit
            hideShow
          />
          <PaginationWrapper>
            <DefaultPagination
              count={pagedUsersVerifyCodes.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </PaginationWrapper>
          <Dialog
            open={open}
            onConfirm={onConfirm}
            onCancel={onClose}
            message={t('user:verifyCodes.delete.confirm', { code: itemToDelete?.code, username: itemToDelete?.username })}
          />
        </div>
      )}
    </Container>
  );
};
